import React, { useContext, useEffect, useMemo, useState } from "react";
import { CustomDialog } from "../../../../dialogs";
import { SearchInput } from "../../../../text-fields";
import { capitalize, isEqual, isNonEmptyArray, multiSort } from "../../../../../utils";
import styles from "./CustomFormTree.module.css";
import ExpandableRow from "./ExpandableRow";
import { translate } from "../../../../../providers";
import { CustomTooltip, icon, IconComponent } from "../../../..";
import { useApi } from "../../../../../hooks";
import { CustomizableProjectPagesService } from "../../../../../../api";
import { SectionsContext } from "../../../CustomizableForm";
import { getFilteredItems } from "./utils";

const filterNestedArray = (input, data) => {
	if (!input) {
		return data;
	}
	const lowercasedInput = input.toLowerCase();
	function filterHelper(items) {
		return items
			.map((item) => {
				const hasMatchingDescendants = item.subitems && filterHelper(item.subitems).length > 0;
				const isMatch = item.name.toLowerCase().includes(lowercasedInput);
				if (isMatch || hasMatchingDescendants) {
					return {
						...item,
						subitems: hasMatchingDescendants ? filterHelper(item.subitems) : [],
					};
				}
				return null;
			})
			.filter(Boolean);
	}
	return filterHelper(data);
};

export default function CustomFormTree({
	block,
	customPageId,
	hasEditorRights = false,
	projectId,
	sectionId,
	onGlobalUpdate,
}) {
	const { layoutIndex, updateLocalSection } = useContext(SectionsContext);
	const [localValue, setLocalValue] = useState([]);
	const [tempLocalValue, setTempLocalValue] = useState([]);
	const [openDialog, setOpenDialog] = useState(false);
	const [searchValue, setSearchValue] = useState("");
	const {
		currentValue,
		currentValueIds,
		items: allItems,
		multipleChoice,
		optionDependencies,
		isMandatory,
		mandatory,
	} = block.options;
	const { name, id } = block;
	const { call: updateTreeCustomPage } = useApi(CustomizableProjectPagesService.updateTreeCustomPage);
	const valueHasUpdated = useMemo(() => !isEqual(localValue, tempLocalValue), [localValue, tempLocalValue]);
	const items = useMemo(() => {
		if (isNonEmptyArray(optionDependencies)) {
			const getSelectedOption = (dependencyId) => layoutIndex[dependencyId]?.value;
			return getFilteredItems({ items: allItems, optionDependencies }, getSelectedOption);
		}
		return allItems;
	}, [allItems, optionDependencies, layoutIndex]);
	const disabled = useMemo(
		() =>
			isNonEmptyArray(optionDependencies) &&
			!optionDependencies.some((dep) => layoutIndex[dep.fieldId]?.value !== undefined),
		[optionDependencies, layoutIndex]
	);
	useEffect(() => {
		setLocalValue(currentValueIds || (currentValue && [currentValue]) || []);
		setTempLocalValue(currentValueIds || (currentValue && [currentValue]) || []);
	}, [currentValue, currentValueIds]);
	const handleOpenDialog = () => {
		if (!openDialog && !disabled) {
			setOpenDialog(true);
		}
	};
	const handleCloseDialog = () => {
		setOpenDialog(false);
	};
	const handleClearSearchValue = () => {
		setSearchValue("");
	};
	const handleChangeSearchValue = (event) => {
		const { value } = event.target;
		setSearchValue(value);
	};
	const handleIsChecked = (item) => tempLocalValue.includes(item.uuid);
	const handleCheck = (item) => {
		if (multipleChoice) {
			if (tempLocalValue.includes(item.uuid)) {
				setTempLocalValue((prev) => prev.filter((savedItem) => savedItem !== item.uuid));
			} else {
				setTempLocalValue((prev) => [...prev, item.uuid]);
			}
		} else if (!tempLocalValue.includes(item.uuid)) {
			setTempLocalValue([item.uuid]);
		} else {
			setTempLocalValue([]);
		}
	};
	const handleSubmit = () => {
		if ((isMandatory || mandatory) && tempLocalValue.length === 0) {
			return;
		}
		if (!isEqual(tempLocalValue, localValue) && id) {
			const toBeAdded = tempLocalValue || [];
			const toBeDeleted = localValue.filter((old) => !tempLocalValue.some((tlv) => tlv === old)) || [];
			if (onGlobalUpdate && typeof onGlobalUpdate === "function") {
				onGlobalUpdate({
					uuid: id,
					value: {
						toBeAdded: (isNonEmptyArray(toBeAdded) && toBeAdded) || null,
						toBeDeleted: (isNonEmptyArray(toBeDeleted) && toBeDeleted) || null,
					},
				});
				setLocalValue(tempLocalValue);
				const currentType = (currentValue && "currentValue") || "currentValueIds";
				updateLocalSection(sectionId, id, { [currentType]: tempLocalValue });
				handleCloseDialog();
			} else {
				updateTreeCustomPage(
					{ customPageId, projectId },
					{
						fieldUuid: id,
						toBeAdded: (isNonEmptyArray(toBeAdded) && toBeAdded) || null,
						toBeDeleted: (isNonEmptyArray(toBeDeleted) && toBeDeleted) || null,
					}
				)
					.then(() => {
						setLocalValue(tempLocalValue);
						updateLocalSection(sectionId, id, { currentValueIds: tempLocalValue });
					})
					.catch((error) => {
						console.error(error);
						setTempLocalValue(localValue);
					})
					.finally(handleCloseDialog);
			}
		}
	};
	const displayList = useMemo(() => {
		const result = [];
		function recurse(obj) {
			obj.forEach((item) => {
				if (localValue.some((lValue) => lValue === item.uuid)) {
					result.push(item.name);
				}

				if (isNonEmptyArray(item.subitems)) {
					recurse(item.subitems);
				}
			});
		}
		recurse(items);
		return <p>{(isNonEmptyArray(result) && result[0]) || "-"}</p>;
	}, [items, localValue]);
	return (
		<div className={styles.container} data-is-disabled={disabled} role="presentation" onClick={handleOpenDialog}>
			<div
				className={styles.selectedText}
				data-has-multiple={localValue?.length > 1}
				data-has-value={localValue && isNonEmptyArray(localValue)}
				data-is-disabled={disabled}
				data-testid={`form.item.${name}`}
			>
				<CustomTooltip title={isNonEmptyArray(localValue) && displayList}>
					<span
						className={styles.nameList}
						data-has-multiple={isNonEmptyArray(localValue) && localValue.length > 1}
					>
						{(disabled &&
							translate("common:input.selector.dependancy-option.select", {
								option: layoutIndex[optionDependencies?.[0]?.fieldId]?.name,
							})) ||
							(isNonEmptyArray(localValue) && displayList) ||
							(hasEditorRights && (
								<p>{translate("common:input.selector.label", { label: name?.toLowerCase() })}</p>
							)) || <p>{translate("common:empty-state.none")}</p>}
					</span>
				</CustomTooltip>
				{localValue?.length > 1 && (
					<span>
						{translate(
							(localValue.length > 2 && "common:input.selector.label.multiple.counter") ||
								"common:input.selector.label.one-more.counter",
							{ counter: localValue.length - 1 }
						)}
					</span>
				)}
				{!disabled && <IconComponent color="var(--grey-500)" icon={icon.faChevronDown} />}
			</div>
			<CustomDialog
				displayCloseCrosss
				isForm
				open={openDialog}
				submitDisabled={
					!valueHasUpdated || !hasEditorRights || ((isMandatory || mandatory) && tempLocalValue.length === 0)
				}
				title={capitalize(name)}
				onClose={handleCloseDialog}
				onSubmit={handleSubmit}
			>
				<SearchInput
					fullWidth
					className={styles.searchContainer__textfield}
					value={searchValue}
					onChange={handleChangeSearchValue}
					onClearSearch={handleClearSearchValue}
				/>
				<div className={styles.tree}>
					{isNonEmptyArray(items) &&
						filterNestedArray(searchValue, multiSort(items, ["name"])).map((item) => (
							<ExpandableRow
								key={item.uuid}
								checkedItems={tempLocalValue}
								item={item}
								readOnly={!hasEditorRights || block?.readOnly}
								onCheck={handleCheck}
								onIsChecked={handleIsChecked}
							/>
						))}
				</div>
			</CustomDialog>
		</div>
	);
}
